import { useState, useEffect } from 'react'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-breakout')

interface Participant { identity: string; name: string }

interface BreakoutSetupProps {
  roomId: string
  participants: Participant[]
  onStart: (count: number, assignments: Record<string, number>, timerMin?: number) => void
  onClose: () => void
}

export function BreakoutSetup({ roomId, participants, onStart, onClose }: BreakoutSetupProps) {
  const [count, setCount] = useState(2)
  const [assignments, setAssignments] = useState<Record<string, number>>({})
  const [timerMin, setTimerMin] = useState<number | undefined>(undefined)
  const [mode, setMode] = useState<'auto' | 'manual'>('auto')

  // Auto-assign on count or mode change
  useEffect(() => {
    if (mode === 'auto') {
      const a: Record<string, number> = {}
      participants.forEach((p, i) => { a[p.identity] = (i % count) + 1 })
      setAssignments(a)
    }
  }, [count, mode, participants])

  const handleStart = () => {
    // Ensure all participants have assignments
    const finalAssignments: Record<string, number> = {}
    participants.forEach((p, i) => {
      finalAssignments[p.identity] = assignments[p.identity] ?? (i % count) + 1
    })
    slog.action('breakout-start', { roomId, count, mode, timerMin, participants: participants.length })
    onStart(count, finalAssignments, timerMin)
  }

  // Group participants by room for preview
  const groups: Record<number, Participant[]> = {}
  for (let n = 1; n <= count; n++) groups[n] = []
  participants.forEach(p => {
    const n = assignments[p.identity] ?? 1
    if (groups[n]) groups[n].push(p)
  })

  return (
    <div className="absolute inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-[420px] max-h-[80vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
          <h3 className="text-sm font-semibold text-zinc-200">Sous-salles</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-lg leading-none">&times;</button>
        </div>

        <div className="px-4 py-3 space-y-4">
          {/* Count */}
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Nombre de sous-salles</label>
            <input
              type="number" min={2} max={10} value={count}
              onChange={e => setCount(Math.max(2, Math.min(10, Number(e.target.value) || 2)))}
              className="w-20 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-200 outline-none focus:border-blue-500"
            />
          </div>

          {/* Mode toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setMode('auto')}
              className={`px-3 py-1.5 text-xs rounded ${mode === 'auto' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200'}`}
            >
              Automatique
            </button>
            <button
              onClick={() => setMode('manual')}
              className={`px-3 py-1.5 text-xs rounded ${mode === 'manual' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200'}`}
            >
              Manuelle
            </button>
          </div>

          {/* Timer */}
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Timer (minutes, optionnel)</label>
            <input
              type="number" min={1} max={120} placeholder="Pas de limite"
              value={timerMin ?? ''}
              onChange={e => setTimerMin(Number(e.target.value) || undefined)}
              className="w-28 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:border-blue-500"
            />
          </div>

          {/* Groups preview */}
          <div className="space-y-2">
            {Array.from({ length: count }, (_, i) => i + 1).map(n => (
              <div key={n} className={`bg-zinc-800 rounded p-2 ${(groups[n] || []).length === 0 ? 'opacity-50' : ''}`}>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1">Salle {n} {(groups[n] || []).length > 0 ? `(${(groups[n] || []).length})` : ''}</div>
                {(groups[n] || []).length === 0 && (
                  <div className="text-[10px] text-zinc-600 italic">Vide</div>
                )}
                {(groups[n] || []).map(p => (
                  <div key={p.identity} className="flex items-center justify-between py-0.5">
                    <span className="text-xs text-zinc-300 truncate">{p.name}</span>
                    {mode === 'manual' && (
                      <select
                        value={assignments[p.identity] ?? n}
                        onChange={e => setAssignments(prev => ({ ...prev, [p.identity]: Number(e.target.value) }))}
                        className="ml-2 px-1 py-0.5 bg-zinc-700 border border-zinc-600 rounded text-[10px] text-zinc-300"
                      >
                        {Array.from({ length: count }, (_, j) => j + 1).map(s => (
                          <option key={s} value={s}>Salle {s}</option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-zinc-700">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs bg-zinc-700 text-zinc-300 hover:bg-zinc-600 rounded"
          >
            Annuler
          </button>
          <button
            onClick={handleStart}
            disabled={participants.length === 0}
            className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded"
          >
            Demarrer ({count} salles)
          </button>
        </div>
      </div>
    </div>
  )
}
